/* eslint-disable @typescript-eslint/no-empty-function */
import React, { Dispatch, ReactNode, SetStateAction, useMemo } from "react";

export interface IDataForm {
  traceId?: string;
  registrationId?: string;
  acceptPrivacyPolicy: boolean;
  admId: number;
  admName: string | undefined;
  isPartnerAdm: boolean | undefined;
  isWorkableAdm: boolean | undefined;
  admPatterns: AdmPatterns;
  name: string;
  phone: string;
  email: string;
  isPj: boolean;
  companyName: string;
  formStep: number;
  metadata: IGenericJSON | IFormMetaData;
  cpf: string;
  lgpd?: boolean;
  group?: string;
  share?: string;
  contractId?: string | null;
  cnpj: string;
  companyOwners: CompanyOwner[];
  goodType?: number;
  value?: number;
  ownedAmount?: number;
  remainingPayments?: number;
  isAwarded?: string;
  isCanceled?: string;
  awardedDate?: string;
  attachmentName?: string;
  attachmentPath?: string;
  groupOptions?: string[];
}

export interface AdmPatterns {
  groupPattern: string | undefined;
  sharePattern: string | undefined;
}

export interface IGenericJSON {
  [key: string]: unknown;
}

export interface IFormMetaData {
  date: string;
  timezone: number;
  hostname: string;
  pathname: string;
  referrer: string;
  historyLength: number;
  queryParams: Record<string, unknown>;
  cookies: string;
  localstorage: Record<string, string>;
  formData: IFormMetaDataTimes;
  browser: {
    appCodeName: string;
    appVersion: string;
    language: string;
    vendor: string;
  };
  os: {
    platform: string;
  };
}

export interface IFormMetaDataTimes {
  startedForm?: string;
  acceptedPrivacyAndPolicyTerms?: string;
  advancedToStepTwo?: string;
  acceptedPartnerDataTerms?: string;
  advancedToStepThree?: string;
  finishedForm?: string;
}

export interface CompanyOwner {
  cpf: string;
  name: string;
  email: string;
}

interface DataContextProviderProps {
  children: ReactNode;
}

export interface IAdm {
  id?: string;
  parent?: string;
  slug: string;
  api: string | null;
  name?: string;
  isPartner: boolean;
  askForStatement: boolean;
  workable: boolean;
  companyName: string;
  groupPattern: string;
  sharePattern: string;
  groupList: Array<string>;
  groupDescription: string;
  shareNumberDescription: string;
  useAPI?: boolean;
  caasEnable?: boolean;
}

export type FormVariations =
  | "all"
  | "partner"
  | "partnerWithStatement"
  | "workable"
  | "nonworkable";

export interface IDataFormContext {
  dataForm: IDataForm;
  metaDataTimes: IFormMetaDataTimes;
  admsList: Array<IAdm>;
  currentAdm: IAdm;
  currentFormStep: number;
  currentFormVariation: FormVariations;
  isPj: boolean;
  alreadyShowed: boolean;
  askForStatement: boolean;
  blockNonPartnerFlow: boolean;
  platformUrl: string;
  setDataForm: Dispatch<SetStateAction<IDataForm>>;
  setMetaDataTimes: Dispatch<SetStateAction<IFormMetaDataTimes>>;
  setAdmsList: Dispatch<SetStateAction<Array<IAdm>>>;
  setCurrentAdm: Dispatch<SetStateAction<IAdm>>;
  setCurrentFormStep: Dispatch<SetStateAction<number>>;
  setCurrentFormVariation: Dispatch<SetStateAction<FormVariations>>;
  setIsPj: Dispatch<SetStateAction<boolean>>;
  setAskForStatement: Dispatch<SetStateAction<boolean>>;
  setAlreadyShowed: Dispatch<SetStateAction<boolean>>;
  setPlatformUrl: Dispatch<SetStateAction<string>>;
}

export const initialContextState: IDataFormContext = {
  dataForm: {} as IDataForm,
  metaDataTimes: {} as IFormMetaDataTimes,
  admsList: [] as Array<IAdm>,
  currentAdm: {} as IAdm,
  currentFormStep: 0,
  currentFormVariation: "all",
  isPj: false,
  alreadyShowed: false,
  askForStatement: false,
  blockNonPartnerFlow: false,
  platformUrl: "",
  setDataForm: () => {},
  setMetaDataTimes: () => {},
  setAdmsList: () => [],
  setCurrentAdm: () => {},
  setCurrentFormStep: () => 0,
  setCurrentFormVariation: () => "all",
  setIsPj: () => false,
  setAskForStatement: () => false,
  setAlreadyShowed: () => false,
  setPlatformUrl: () => "",
};

export const DataFormContext = React.createContext(initialContextState);

/**
 * Return Data Form context provider
 * @param {SignerContextProviderProps} children
 * @return {React.JSX.Element} return a element
 */
export function DataFormContexProvider({
  children,
}: DataContextProviderProps): React.JSX.Element {
  const [dataForm, setDataForm] = React.useState({} as IDataForm);
  const [metaDataTimes, setMetaDataTimes] = React.useState(
    {} as IFormMetaDataTimes
  );
  const [admsList, setAdmsList] = React.useState([] as Array<IAdm>);
  const [currentAdm, setCurrentAdm] = React.useState({} as IAdm);
  const [currentFormStep, setCurrentFormStep] = React.useState(0);
  const [currentFormVariation, setCurrentFormVariation] = React.useState(
    "all" as FormVariations
  );
  const [isPj, setIsPj] = React.useState(false);
  const [askForStatement, setAskForStatement] = React.useState(false);
  const [alreadyShowed, setAlreadyShowed] = React.useState(false);
  const [platformUrl, setPlatformUrl] = React.useState("");
  // Adding a flag to control the non partner behavior in case we change our minds latter
  const blockNonPartnerFlow = true;

  const contextValue: IDataFormContext = useMemo(
    () => ({
      dataForm,
      metaDataTimes,
      admsList,
      currentAdm,
      currentFormStep,
      currentFormVariation,
      isPj,
      alreadyShowed,
      askForStatement,
      blockNonPartnerFlow,
      platformUrl,
      setDataForm,
      setMetaDataTimes,
      setAdmsList,
      setCurrentAdm,
      setCurrentFormStep,
      setCurrentFormVariation,
      setIsPj,
      setAskForStatement,
      setAlreadyShowed,
      setPlatformUrl,
    }),
    [
      dataForm,
      metaDataTimes,
      admsList,
      currentAdm,
      currentFormStep,
      currentFormVariation,
      isPj,
      alreadyShowed,
      askForStatement,
      blockNonPartnerFlow,
      platformUrl,
    ]
  );

  return (
    <DataFormContext.Provider value={contextValue}>
      {children}
    </DataFormContext.Provider>
  );
}

/**
 * Return dataForm context provider
 * @return {IDataFormContextany} return a context
 */
export function useDataFormContext(): IDataFormContext {
  return React.useContext(DataFormContext);
}

export default DataFormContext;
